import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { FileHandle } from 'src/app/directives/Drag/drag.directive';
import { UploadFileDialogData, MediaFile, FileNode, ErrorCodes } from 'src/app/utils/common-classes';
import { UploadService } from 'src/app/services/Upload/upload.service';
import { UtilsService } from 'src/app/services/Utils/utils.service';
import { FileService } from 'src/app/services/File/file.service';
import { ManagedUpload } from 'aws-sdk/clients/s3';
import { Subject, Subscription } from 'rxjs';
@Component({
  selector: 'app-upload-files-dialog',
  templateUrl: './upload-files-dialog.component.html',
  styleUrls: ['./upload-files-dialog.component.scss']
})
export class UploadFileDialogComponent implements OnInit, OnDestroy {
  public selectedFile: File;
  public folderId: string;
  public arrayFiles: Array<any> = [];
  public uploading: boolean = false;
  public uploadingPreview: boolean = false;
  public aborted: boolean = false;
  public currentFile: number;
  public totalFiles: number;
  public currentUploadProgress: number = 0;
  private abortUploadSubject: Subject<void>;
  private lastUpdate: number = new Date().getTime();
  constructor(public dialogRef: MatDialogRef<UploadFileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UploadFileDialogData,
    public uploadService: UploadService,
    public utils: UtilsService,
    private fileService: FileService) {

    this.folderId = data.folder.mediaFolderId;
  }

  ngOnInit() {
    this.abortUploadSubject = new Subject<void>();
  }

  ngOnDestroy() {
    this.abortUploadSubject.complete();
  }

  public closeDialog(): void {
    this.cancel();
    this.dialogRef.close();
  };

  public cancel(): void {
    this.aborted = true;
    this.abortUploadSubject.next();
  }

  public onFileChanged(event) {
    let files = event.target.files;
    if (files) {
      for (let file of files) {
        this.arrayFiles.push(file);
      }
    }
  }

  public removeFile(event: Event, file) {
    event.stopPropagation();
    let fileIndex: number = this.arrayFiles.indexOf(file);
    if (fileIndex > -1) {
      this.arrayFiles.splice(fileIndex, 1);
    }
  }

  public filesDropped(files: FileHandle[]): void {
    if (files) {
      for (let fl of files) {
        this.arrayFiles.push(fl.file);
      }
    }
  }

  public async upload(): Promise<void> {
    let _self = this;
    if (!_self.arrayFiles || _self.arrayFiles.length <= 0) {
      let message: string = await _self.utils.translateKey("dialog.upload-files.error.no-file-selected-error");
      _self.utils.showErrorToast(message);
      return Promise.resolve();
    }
    _self.uploading = true;
    _self.utils.setDialogUnclosable(true);
    let errors: Array<FileNode> = [];
    let uploadedFiles: Array<MediaFile> = [];
    for (let i = 0; i < _self.arrayFiles.length; i++) {
      let file = _self.arrayFiles[i];
      _self.currentFile = i;
      _self.totalFiles = _self.arrayFiles.length;
      _self.currentUploadProgress = 0;
      try {
        let data: ManagedUpload.SendData = await _self.uploadService.uploadFile(file, 'tmp/media/media-file/', _self.abortUploadSubject, (progress: ManagedUpload.Progress) => {
          if (new Date().getTime() - _self.lastUpdate > 250) {
            _self.currentUploadProgress = parseFloat(((progress.loaded * 100) / progress.total).toFixed(2));
            _self.lastUpdate = new Date().getTime();
          }
        });
        let uploadedFile: MediaFile = {
          mediaFileId: null,
          name: file.name,
          mimeType: file.type,
          mediaFolderId: _self.folderId,
          url: data.Key,
          previewUrl: null,
          attributes: "",
          description: "",
          storageType: "S3",
          translations: null
        }

        _self.currentUploadProgress = 100;
        _self.uploadingPreview = true;
        let mediaFile: MediaFile = await _self.fileService.createFile(uploadedFile);
        let updatedMediaFile = await _self.controlImage(file, mediaFile);
        _self.uploadingPreview = false;
        uploadedFiles.push(updatedMediaFile);
        if (_self.aborted) {
          throw ErrorCodes.UPLOAD_ABORTED;
        }
      } catch (err) {
        if (err == ErrorCodes.UPLOAD_ABORTED) {
          let message: string = await _self.utils.translateKey("dialog.upload-files.error.upload-aborted");
          _self.utils.showErrorToast(message);
          _self.utils.setDialogUnclosable(false);
          _self.dialogRef.close(uploadedFiles);
          return;
        }
        console.error("upload file error", file, err);
        errors.push(file);
      }
    }

    if (errors.length > 0) {
      let filesList: string = "";
      for (let i = 0; i < errors.length; i++) {
        filesList += errors[i].name;
        if (i + 1 != errors.length) {
          filesList += ", "
        }
      }
      let message: string = await _self.utils.translateKeyWithData("dialog.upload-files.error.upload-error", { files: filesList });
      _self.utils.showErrorToast(message);
    } else {
      let message: string = await _self.utils.translateKey("dialog.upload-files.success.upload-success");
      _self.utils.showToast(message);
    }
    _self.utils.setDialogUnclosable(false);
    _self.dialogRef.close(uploadedFiles);
  }

  public controlImage(file: any, mediaFile: MediaFile): Promise<MediaFile> {
    let _self = this;
    if (file.type.indexOf("image") >= 0) {
      return new Promise((resolve, reject) => {
        var img = new Image();
        img.setAttribute("crossorigin", "anonymous");
        img.addEventListener('error', function () {
          return resolve(mediaFile);
        });
        img.onload = function () {
          let canvas = document.createElement("canvas");
          let ctx = canvas.getContext("2d");
          canvas.height = canvas.width * (img.height / img.width);
          // step 1 - resize to 50%
          let oc = document.createElement('canvas');
          let octx = oc.getContext('2d');
          oc.width = img.width * 0.5;
          oc.height = img.height * 0.5;
          octx.drawImage(img, 0, 0, oc.width, oc.height);

          //in caso di png devo risvuotare la trasparenza prima del resize
          let oc2 = document.createElement('canvas');
          let oc2tx = oc2.getContext('2d');
          oc2.width = oc.width;
          oc2.height = oc.height;
          oc2tx.drawImage(oc, 0, 0, oc.width, oc.height);

          // step 2
          octx.clearRect(0, 0, oc.width, oc.height);
          octx.drawImage(oc2, 0, 0, oc2.width * 0.5, oc2.height * 0.5);
          // step 3, resize to final size
          ctx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5,
            0, 0, canvas.width, canvas.height);
          let base64 = canvas.toDataURL();
          _self.uploadService.uploadBase64(base64, 'tmp/preview/media-preview/', _self.abortUploadSubject, null).then((result: string) => {
            mediaFile.previewUrl = result;
            return _self.fileService.editPreview(mediaFile);
          }).then((data: MediaFile) => {
            resolve(data);
          }).catch((e: Error) => {
            reject(e);
          });
        }
        img.src = mediaFile.url;
      });
    }

    if (file.type.indexOf("video") >= 0) {
      return new Promise((resolve, reject) => {
        let video = document.createElement('video');
        video.setAttribute("crossorigin", "anonymous");
        video.addEventListener('error', function () {
          return resolve(mediaFile);
        });
        video.onloadedmetadata = function () {
          let duration = video.duration;
          let middle = duration / 2;
          middle = Math.floor(middle);
          video.currentTime = middle;
        };

        video.onseeked = function (e) {
          let canvas = document.createElement('canvas');
          canvas.height = video.videoHeight;
          canvas.width = video.videoWidth;
          let ctx = canvas.getContext('2d');
          //ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // step 1 - resize to 50%
          let oc = document.createElement('canvas');
          let octx = oc.getContext('2d');
          oc.height = video.videoHeight * 0.5;
          oc.width = video.videoWidth * 0.5;
          octx.drawImage(video, 0, 0, oc.width, oc.height);
          // step 2
          octx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5);
          // step 3, resize to final size
          ctx.drawImage(oc, 0, 0, oc.width * 0.5, oc.height * 0.5,
            0, 0, canvas.width, canvas.height);

          let base64 = canvas.toDataURL();
          _self.uploadService.uploadBase64(base64, 'tmp/preview/media-preview/', _self.abortUploadSubject, null).then((result: string) => {
            mediaFile.previewUrl = result;
            return _self.fileService.editPreview(mediaFile);
          }).then((data: any) => {
            resolve(data);
          }).catch((e: Error) => {
            reject(e);
          });
        };

        video.src = mediaFile.url;
      });
    }

    return Promise.resolve(mediaFile);
  }
}
